import React, { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Hexagon, CheckCircle2, Clock } from "lucide-react";
import { api } from "@/lib/api";
import { BentoCard } from "@/components/dashboard/BentoCard";
import { HexBadge } from "@/components/dashboard/Hex";

export const COVERAGE = {
  root: "coverage-page",
  stateCard: (s) => `coverage-state-${s}`,
};

export default function CoveragePage() {
  const { data } = useQuery({
    queryKey: ["coverage"],
    queryFn: async () => (await api.get("/coverage")).data,
    staleTime: 60_000,
  });
  const [query, setQuery] = useState("");

  const states = data?.states || [];
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return states;
    return states.filter(
      (row) =>
        row.state.toLowerCase().includes(q) ||
        row.cities.some((c) => c.city.toLowerCase().includes(q))
    );
  }, [states, query]);

  const pct = data ? Math.round((data.live_cities / data.total_cities) * 100) : 0;

  return (
    <div data-testid={COVERAGE.root} className="space-y-8">
      <div>
        <div className="data-label">Coverage</div>
        <h1 className="font-display text-4xl font-bold tracking-tight text-red-600">
          Where Lien Scout is live.
        </h1>
        <p className="text-sm text-zinc-600 mt-2 max-w-2xl">
          Every US state, top 5 largest cities each. We're rolling out live
          adapters county-by-county — new cities light up as their public data
          feeds are wired in.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <BentoCard accent>
          <span className="data-label">Live Cities</span>
          <div className="mt-3 font-display text-5xl font-bold text-red-600">
            {data?.live_cities ?? 0}
          </div>
          <div className="font-mono-data text-xs text-zinc-500 uppercase tracking-widest mt-1">
            of {data?.total_cities ?? 0}
          </div>
        </BentoCard>
        <BentoCard>
          <span className="data-label">Coverage</span>
          <div className="mt-3 font-display text-5xl font-bold">{pct}%</div>
          <div className="w-full h-2 bg-zinc-100 border-2 border-black mt-3">
            <div
              className="h-full bg-yellow-400 border-r-2 border-black"
              style={{ width: `${pct}%` }}
            />
          </div>
        </BentoCard>
        <BentoCard>
          <span className="data-label">States Tracked</span>
          <div className="mt-3 font-display text-5xl font-bold">
            {data?.total_states ?? 0}
          </div>
          <div className="font-mono-data text-xs text-zinc-500 uppercase tracking-widest mt-1">
            all 50 + DC
          </div>
        </BentoCard>
        <BentoCard>
          <span className="data-label">Live Sources</span>
          <div className="mt-3 font-display text-5xl font-bold">
            {data?.live_sources?.length ?? 0}
          </div>
          <div className="font-mono-data text-xs text-zinc-500 uppercase tracking-widest mt-1">
            county feeds
          </div>
        </BentoCard>
      </div>

      <div className="relative">
        <input
          type="text"
          placeholder="Search state or city…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          data-testid="coverage-search"
          className="w-full md:w-96 px-4 py-2 border-2 border-black bg-white text-sm font-mono-data focus:outline-none focus:border-yellow-500"
        />
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {filtered.map((row) => (
          <StateCard key={row.state} row={row} />
        ))}
      </div>
    </div>
  );
}

function StateCard({ row }) {
  const liveCount = row.cities.filter((c) => c.live).length;
  return (
    <BentoCard data-testid={COVERAGE.stateCard(row.state)} accent={liveCount > 0}>
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-2">
          <Hexagon
            size={22}
            strokeWidth={2}
            className={liveCount > 0 ? "text-red-600 fill-yellow-100" : "text-zinc-400 fill-zinc-100"}
          />
          <div className="font-display font-bold text-2xl">{row.state}</div>
        </div>
        {liveCount > 0 ? (
          <HexBadge tone="success">{liveCount}/{row.cities.length} LIVE</HexBadge>
        ) : (
          <HexBadge>Coming Soon</HexBadge>
        )}
      </div>
      <ul className="mt-3 space-y-1.5 text-sm">
        {row.cities.map((c) => (
          <li key={c.city} className="flex items-center gap-2">
            {c.live ? (
              <CheckCircle2 size={14} className="text-emerald-600" strokeWidth={2.5} />
            ) : (
              <Clock size={14} className="text-zinc-400" strokeWidth={2} />
            )}
            <span className={c.live ? "font-semibold" : "text-zinc-500"}>{c.city}</span>
          </li>
        ))}
      </ul>
    </BentoCard>
  );
}
